import { Org } from 'types/org';
import { TeamLite } from 'types/properties';
import {
  StockLocationSchemaLite,
  StockLocationPurpose,
} from 'types/clientV2/stockLocations';
import { HandlingRequestSchemaLite } from 'types/clientV2/stock';
import {
  MoveWarningData,
  MoveWarningType,
  StockLotSubset,
  HandlingRequestSubset,
} from 'views/warehouse/MoveWarningContext/types';
import {
  getNextHandlingRequest,
  matchHandlingRequestToLocationAssignment,
  requestTypeToPurpose,
} from 'views/warehouse/utils';

// TODO: I don't want to be doing this but the scan endpoint returns the old StockLotCRUDSchema. remove this once we have the new schema
// https://linear.app/cofactr/issue/UI-4662/update-all-scanned-stock-lot-types-to-support-non-crud-schema
/**
 * Safely extracts the organization ID from various org representations
 * @param org - Either a string representing the org ID, an Org object, or null/undefined
 * @returns The organization ID as a string, or undefined if org is null/undefined or has no valid id
 */
export const getOrgId = (org: Org['id'] | Org): string | undefined => {
  if (!org) return undefined;
  if (typeof org === 'string') return org;
  if (typeof org === 'object' && org.id) {
    return typeof org.id === 'string' ? org.id : undefined;
  }
  return undefined;
};

/**
 * Safely extracts the team ID from various team representations
 * @param team - Either a string representing the team ID, a TeamLite object, or null/undefined
 * @returns The team ID as a string, or undefined if team is null/undefined or has no valid id
 */
export const getTeamId = (
  team: string | TeamLite | null | undefined
): string | undefined => {
  if (!team) return undefined;
  if (typeof team === 'string') return team;
  if (typeof team === 'object' && team.id) {
    return typeof team.id === 'string' ? team.id : undefined;
  }
  return undefined;
};

/**
 * Pure utility function to validate stock lot moves
 * @param stockLot - The stock lot to validate
 * @param destination - The destination location
 * @param handlingRequest - Optional handling request
 * @param openHandlingRequests - Array of open handling requests for the stock lot
 * @param facility - The current facility
 * @param isUsingTeams - Whether the facility is using teams instead of orgs
 * @returns MoveWarningData if validation fails, null if validation passes
 */
export const validateMoveLogic = ({
  stockLot,
  destination,
  handlingRequest,
  openHandlingRequests,
  facility,
  isUsingTeams,
}: {
  stockLot: StockLotSubset;
  destination: StockLocationSchemaLite;
  handlingRequest: HandlingRequestSubset | null | undefined;
  openHandlingRequests: HandlingRequestSchemaLite[];
  facility: StockLocationSchemaLite | undefined;
  isUsingTeams: boolean;
}): MoveWarningData | null => {
  const nextHandlingRequest =
    handlingRequest || getNextHandlingRequest(openHandlingRequests);

  if (
    destination.purpose === null &&
    nextHandlingRequest &&
    requestTypeToPurpose[nextHandlingRequest.requestType]
  ) {
    return {
      stockLot,
      destination,
      reason: MoveWarningType.UNASSIGNED_BIN,
      handlingRequest: nextHandlingRequest,
    };
  }

  if (destination.purpose === null && !nextHandlingRequest) {
    return {
      stockLot,
      destination,
      reason: MoveWarningType.UNASSIGNED_BIN,
      handlingRequest: null,
    };
  }

  const stockLotOrgId = getOrgId(stockLot.org);
  const destinationOrgId = destination.org;
  const stockLotTeamId = getTeamId(stockLot.team);
  const destinationTeamId = getTeamId(destination.team);

  if (
    destination.purpose !== null &&
    [StockLocationPurpose.STORAGE, StockLocationPurpose.PUTTING].includes(
      destination.purpose
    ) &&
    ((isUsingTeams && destinationTeamId !== stockLotTeamId) ||
      (!isUsingTeams &&
        destinationOrgId !== stockLotOrgId &&
        destinationOrgId !== facility?.org))
  ) {
    return {
      stockLot,
      destination,
      reason: MoveWarningType.CUSTOMER_MISMATCH,
      handlingRequest: nextHandlingRequest,
    };
  }

  const result = matchHandlingRequestToLocationAssignment(
    nextHandlingRequest,
    destination,
    true
  );

  if (
    destination.purpose !== null &&
    typeof result === 'object' &&
    !result.matched
  ) {
    return {
      stockLot,
      destination,
      reason: MoveWarningType.ASSIGNMENT_MISMATCH,
      handlingRequest: nextHandlingRequest,
      mismatchReasons: result.mismatches,
    };
  }

  return null;
};
